"use client";

import * as React from "react";
import { Check, MapPin, Minus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
const services = [{
  id: "design",
  name: "Design & Planering"
}, {
  id: "vvs",
  name: "VVS & Rördragning"
}, {
  id: "tiling",
  name: "Kakel & Klinker"
}, {
  id: "electric",
  name: "Elinstallation"
}, {
  id: "project",
  name: "Projektledning"
}];
const areas = [{
  id: "karlstad-c",
  code: "Karlstad",
  name: "Karlstad Centrum",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "hammaro",
  code: "Hammarö",
  name: "Hammarö & Skoghall",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "skare",
  code: "Skåre",
  name: "Skåre & Grava",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "kronoparken",
  code: "Kronoparken",
  name: "Kronoparken",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "vaxnas",
  code: "Våxnäs",
  name: "Våxnäs Industri",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "farjestad",
  code: "Färjestad",
  name: "Färjestad & Rud",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}, {
  id: "villaomraden",
  code: "Värmland",
  name: "Villaområden",
  design: true,
  vvs: true,
  tiling: true,
  electric: true,
  project: true
}];
export default function Localserviceareas() {
  return <section data-section-id="262410" id="localserviceareas" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
          <Badge variant="secondary" className="mb-2 bg-accent text-accent-foreground font-default">Lokal Expertis</Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight font-default text-foreground">
            Badrumsrenovering i Karlstad med lokal projektledning
          </h2>
          <p className="max-w-[700px] text-muted-foreground md:text-lg font-default">Vi hjälper dig med renovering av badrum i Karlstad och flera närliggande områden. Med samordnad totalentreprenad planerar vi projektets olika delar, från första hembesöket till installation, våtrumsarbete och färdigt badrum.</p>
        </div>

        <div className="mx-auto max-w-5xl">
          <Card className="border-border shadow-lg rounded-lg overflow-hidden">
            <CardHeader className="bg-muted/50 border-b border-border pb-4">
              <CardTitle className="font-default text-xl text-foreground">Tillgänglighetsmatris</CardTitle>
              <CardDescription className="font-default">
                Hitta ditt område för att se tillgängliga tjänster för badrumsrenovering.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="w-full whitespace-nowrap rounded-b-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="w-[250px] sticky left-0 bg-card z-10 border-r border-border font-default text-foreground">
                        Område / Region
                      </TableHead>
                      {services.map((service, i) => <TableHead key={service.id} data-index={i} className="text-center min-w-[140px] font-default text-foreground">
                          {service.name}
                        </TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {areas.map((area, i) => <TableRow key={area.id} data-index={i} className="border-border hover:bg-accent/30 transition-colors">
                        <TableCell className="font-medium sticky left-0 bg-card z-10 border-r border-border">
                          <div className="flex flex-col">
                            <span className="text-foreground font-default">{area.name}</span>
                            <span className="text-xs text-muted-foreground font-default">
                              {area.code}
                            </span>
                          </div>
                        </TableCell>
                        {services.map((service, j) => {
                      const isAvailable = area[service.id as keyof typeof area] as boolean;
                      return <TableCell key={`${area.id}-${service.id}`} data-index={j} className="text-center">
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="inline-flex items-center justify-center w-full h-full cursor-help">
                                      {isAvailable ? <Check className="size-5 text-primary" /> : <Minus className="size-5 text-muted-foreground/30" />}
                                      <span className="sr-only">
                                        {isAvailable ? "Tillgänglig" : "Ej tillgänglig"}
                                      </span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-popover text-popover-foreground border-border shadow-md">
                                    <p className="font-default">
                                      {service.name} är{" "}
                                      <strong className="font-semibold">
                                        {isAvailable ? "tillgängligt" : "ej tillgängligt"}
                                      </strong>{" "}
                                      i {area.name}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>;
                    })}
                      </TableRow>)}
                  </TableBody>
                </Table>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 flex justify-center">
          <Button asChild size="lg" className="rounded-lg px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-default shadow-md transition-all">
            <Link to="/QuoteRequestPage">
              Boka kostnadsfritt hembesök
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}